import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Phone } from "lucide-react";
import { SITE, getServiceBySlug } from "../lib/site";
import { MaskedLine, Reveal } from "../components/Reveal";
import ServiceIcon from "../components/ServiceIcon";
import SectionCTA from "../components/SectionCTA";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    if (!service) return;
    const prevTitle = document.title;
    const prevDesc =
      document.querySelector('meta[name="description"]')?.getAttribute("content") || "";
    document.title = `${service.name} — ${SITE.short} | Philadelphia PA`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && service.metaDescription) meta.setAttribute("content", service.metaDescription);
    return () => {
      document.title = prevTitle;
      if (meta) meta.setAttribute("content", prevDesc);
    };
  }, [service]);

  if (!service) return <Navigate to="/services" replace />;

  const idx = SITE.services.findIndex((s) => s.slug === slug);
  const next = SITE.services[(idx + 1) % SITE.services.length];

  return (
    <>
      {/* HERO */}
      <section
        className="relative min-h-[70svh] md:min-h-[75svh] w-full overflow-hidden pt-24 bg-[#0A192F]"
        data-testid={`service-hero-${service.key}`}
      >
        <div className="absolute inset-0 z-0">
          <img
            src={SITE.images.paperwork}
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-[#0A192F]/75" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A192F]/30 to-[#0A192F]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,_rgba(10,25,47,0.85),_transparent_60%)]" />
        </div>

        <div className="relative z-10 min-h-[calc(70svh-6rem)] md:min-h-[calc(75svh-6rem)] max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10 flex flex-col justify-end pb-14 md:pb-16 text-white">
          <nav aria-label="breadcrumb" className="text-[11px] uppercase tracking-[0.24em] text-white/50 flex items-center gap-2">
            <Link to="/services" className="hover:text-[#C5A059]">Services</Link>
            <span aria-hidden>/</span>
            <span className="text-[#C5A059]">{service.name}</span>
          </nav>

          <div className="mt-8 flex items-center gap-4">
            <span
              className="w-14 h-14 rounded-full flex items-center justify-center"
              style={{ background: service.color, color: service.color === "#0A192F" ? "#C5A059" : "#0A192F" }}
            >
              <ServiceIcon name={service.icon} className="w-6 h-6" />
            </span>
            <span className="label-mini text-[#C5A059]">
              {String(idx + 1).padStart(2, "0")} · Service
            </span>
          </div>

          <h1 className="font-serif mt-5 md:mt-6 text-[10vw] sm:text-[8vw] md:text-[6vw] lg:text-[5.2vw] leading-[0.98] max-w-[16ch] tracking-tight">
            <MaskedLine delay={0.15}>{service.name}</MaskedLine>
          </h1>

          <p className="mt-6 md:mt-8 max-w-2xl text-white/80 text-base md:text-lg font-light leading-relaxed">
            {service.longDescription}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
            <a href={SITE.phoneHref} className="btn-gold justify-center" data-testid="detail-call">
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
            <Link to="/contact" className="btn-ghost justify-center text-white border-white/50" data-testid="detail-contact">
              Ask About This Service <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BENEFITS + DOCS */}
      <section className="py-24 md:py-32 bg-[#FBF9F5]" data-testid={`service-body-${service.key}`}>
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-6">
            <div className="label-mini">Why come to Rhawn</div>
            <h2 className="font-serif mt-5 md:mt-6 text-4xl md:text-5xl leading-[1.05] text-[#0A192F]">
              Fast, thorough, <span className="italic text-[#C5A059]">handled at the counter.</span>
            </h2>
            <ul className="mt-10 space-y-5">
              {service.benefits.map((b, i) => (
                <Reveal key={b} delay={i * 0.06}>
                  <li className="flex items-start gap-4 border-b border-[#0A192F]/12 pb-5">
                    <span className="text-[#C5A059] font-mono-tab text-xs mt-1 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[#0A192F]/85 text-base md:text-lg leading-relaxed">
                      {b}
                    </span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5 lg:col-start-8">
            <div className="border border-[#0A192F]/12 p-8 md:p-10 bg-white sticky top-28">
              <div className="label-mini">What to Bring</div>
              <h3 className="font-serif mt-4 text-2xl md:text-3xl text-[#0A192F]">
                Come prepared for {service.name.toLowerCase()}.
              </h3>
              <ul className="mt-6 space-y-3">
                {service.docs.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-[#0A192F]/85 text-[15px]">
                    <CheckCircle2 className="w-5 h-5 text-[#C5A059] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
              <a href={SITE.phoneHref} className="btn-gold mt-8 w-full justify-center" data-testid="detail-body-call">
                <Phone className="w-4 h-4" /> Call to Confirm Docs
              </a>
              <p className="mt-3 text-[11px] text-[#0A192F]/50 text-center">
                Real person answers · Mon–Sat 9AM–9PM
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NEXT SERVICE */}
      <section className="py-14 bg-white border-t border-[#0A192F]/10" data-testid="service-nav">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <Link to="/services" className="link-under text-sm text-[#0A192F]/70" data-testid="detail-back">
            ← All Services
          </Link>
          <Link
            to={`/services/${next.slug}`}
            className="group inline-flex items-center gap-4 text-right"
            data-testid="detail-next"
          >
            <div>
              <div className="label-mini">Next Service</div>
              <div className="mt-1 font-serif text-2xl md:text-3xl text-[#0A192F] group-hover:text-[#C5A059] transition-colors">
                {next.name}
              </div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-[#0A192F]/60 group-hover:text-[#C5A059] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500" />
          </Link>
        </div>
      </section>

      <SectionCTA
        eyebrow="Ready to get this done?"
        title={<>Call ahead. Walk in. <span className="italic text-[#C5A059]">Walk out finished.</span></>}
        subtitle={`We'll confirm exactly which documents you need for ${service.name.toLowerCase()} — call the counter directly.`}
        secondaryLabel="Get Insurance Quote"
        secondaryTo="/insurance"
        testid="cta-detail-final"
      />
    </>
  );
}
